/**
 * W3C trace-context helpers for Ryn Replay network capture.
 *
 * App Insights stores `operation_Id` as 32 lowercase hex. ASP.NET ProblemDetails
 * `traceId`, the `traceparent` header, and legacy `Request-Id` all have to
 * collapse to that value or the Backend tab queries the wrong rows.
 */

const W3C_TRACEPARENT = /^00-([0-9a-f]{32})-([0-9a-f]{16})-([0-9a-f]{2})$/i;
const HEX_32 = /^[0-9a-f]{32}$/i;

export const RYN_REPLAY_TRACEPARENT_HEADER = 'traceparent';

/** Normalize a header, ProblemDetails.traceId, or already-extracted id. */
export const normalizeRynReplayOperationId = (
  raw: string | null | undefined
): string | undefined => {
  if (!raw || typeof raw !== 'string') {
    return undefined;
  }
  const value = raw.trim();
  const w3c = W3C_TRACEPARENT.exec(value);
  if (w3c) {
    return w3c[1].toLowerCase();
  }
  if (HEX_32.test(value)) {
    return value.toLowerCase();
  }
  const trimmed = value.startsWith('|') ? value.slice(1) : value;
  const root = trimmed.split('.')[0];
  if (HEX_32.test(root)) {
    return root.toLowerCase();
  }
  return undefined;
};

const randomHex = (bytes: number, entropy?: Uint8Array): string => {
  const buf =
    entropy ??
    (typeof globalThis.crypto?.getRandomValues === 'function'
      ? globalThis.crypto.getRandomValues(new Uint8Array(bytes))
      : fallbackEntropy(bytes));
  const slice =
    buf.length >= bytes ? buf.subarray(0, bytes) : fallbackEntropy(bytes);
  return Array.from(slice, (b) => b.toString(16).padStart(2, '0')).join('');
};

/**
 * Last-resort entropy when `crypto.getRandomValues` is unavailable (ancient
 * WebViews). Fine for minting a correlation id the Backend tab matches on —
 * not a security token, not a session secret.
 */
const fallbackEntropy = (bytes: number): Uint8Array => {
  const buf = new Uint8Array(bytes);
  for (let i = 0; i < bytes; i++) {
    buf[i] = Math.floor(Math.random() * 256);
  }
  return buf;
};

/**
 * Mint a sampled W3C traceparent. `entropy` is 24 bytes (16 trace + 8 span)
 * when tests need a stable value.
 */
export const createRynReplayTraceparent = (
  entropy?: Uint8Array
): { traceparent: string; operationId: string } => {
  const operationId = randomHex(16, entropy);
  const spanId = randomHex(
    8,
    entropy && entropy.length >= 24 ? entropy.subarray(16, 24) : undefined
  );
  return {
    traceparent: `00-${operationId}-${spanId}-01`,
    operationId,
  };
};

export interface IRynReplayCorrelationHeaders {
  requestTraceparent?: string | null;
  responseTraceparent?: string | null;
  requestId?: string | null;
  bodyText?: string | null;
}

/**
 * Prefer the id we sent, then the server's response headers, then a
 * ProblemDetails.traceId in the body (Juraj's 4xx case).
 */
export const extractRynReplayOperationId = (
  input: IRynReplayCorrelationHeaders
): string | undefined =>
  normalizeRynReplayOperationId(input.requestTraceparent) ??
  normalizeRynReplayOperationId(input.responseTraceparent) ??
  normalizeRynReplayOperationId(input.requestId) ??
  extractFromProblemDetails(input.bodyText);

const extractFromProblemDetails = (
  bodyText?: string | null
): string | undefined => {
  if (!bodyText || typeof bodyText !== 'string') {
    return undefined;
  }
  const trimmed = bodyText.trim();
  if (!trimmed.startsWith('{')) {
    return undefined;
  }
  try {
    const parsed = JSON.parse(trimmed) as { traceId?: unknown };
    return typeof parsed?.traceId === 'string'
      ? normalizeRynReplayOperationId(parsed.traceId)
      : undefined;
  } catch {
    return undefined;
  }
};

/** Ingest POSTs must not pick up a user-session traceparent. */
export const isRynReplayIngestUrl = (
  url: string,
  ingestUrl?: string
): boolean => {
  if (ingestUrl && url.startsWith(ingestUrl)) {
    return true;
  }
  return /\/api\/ingest(?:\?|$)/i.test(url);
};

export interface IRynReplayTraceparentInjectOptions {
  ingestUrl?: string;
  /**
   * Absolute UPS API bases (`environment.urls.*`, read API, …). Cross-origin
   * requests are stamped only when their origin matches one of these.
   */
  allowedApiBaseUrls?: readonly string[];
  /** Page origin for same-origin checks; defaults to `location.origin`. */
  pageOrigin?: string;
}

/**
 * Whether to inject `traceparent` on this request.
 *
 * Must stay same-origin / allowlisted UPS APIs only. `traceparent` is not a
 * CORS-safelisted request header, so stamping it on arbitrary third-party URLs
 * forces a preflight; any host that does not echo
 * `Access-Control-Allow-Headers: traceparent` fails the real request during a
 * recorded session. Ingest is always skipped.
 */
export const shouldInjectRynReplayTraceparent = (
  url: string,
  options?: IRynReplayTraceparentInjectOptions
): boolean => {
  if (isRynReplayIngestUrl(url, options?.ingestUrl)) {
    return false;
  }

  // Scheme-relative or absolute http(s) need an origin check; everything else
  // (path-relative, same-document) is same-origin by definition.
  const needsOriginCheck = /^(?:[a-z][a-z0-9+.-]*:)?\/\//i.test(url);
  if (!needsOriginCheck) {
    return true;
  }

  const pageOrigin =
    options?.pageOrigin ??
    (typeof globalThis.location?.origin === 'string'
      ? globalThis.location.origin
      : undefined);

  let requestOrigin: string;
  try {
    requestOrigin = new URL(url, pageOrigin ?? 'http://localhost').origin;
  } catch {
    return false;
  }

  if (pageOrigin && requestOrigin === pageOrigin) {
    return true;
  }

  for (const base of options?.allowedApiBaseUrls ?? []) {
    if (!base) {
      continue;
    }
    try {
      if (new URL(base, pageOrigin ?? 'http://localhost').origin === requestOrigin) {
        return true;
      }
    } catch {
      // Bad config entry — skip rather than break capture.
    }
  }

  return false;
};

/**
 * Whether `getResponseHeader` / `Response.headers.get` can read correlation
 * headers without the browser logging "Refused to get unsafe header".
 *
 * Cross-origin responses only expose headers listed in
 * `Access-Control-Expose-Headers`; `traceparent` and `request-id` are almost
 * never exposed, so attempting to read them spams the console on every XHR
 * even inside try/catch. Same-origin and path-relative requests are safe to
 * probe.
 */
export const canReadRynReplayResponseHeaders = (
  url: string,
  options?: Pick<IRynReplayTraceparentInjectOptions, 'pageOrigin'>
): boolean => {
  const needsOriginCheck = /^(?:[a-z][a-z0-9+.-]*:)?\/\//i.test(url);
  if (!needsOriginCheck) {
    return true;
  }

  const pageOrigin =
    options?.pageOrigin ??
    (typeof globalThis.location?.origin === 'string'
      ? globalThis.location.origin
      : undefined);

  try {
    const requestOrigin = new URL(url, pageOrigin ?? 'http://localhost').origin;
    return Boolean(pageOrigin && requestOrigin === pageOrigin);
  } catch {
    return false;
  }
};

export interface IRynReplayTraceparentMergeResult {
  init: RequestInit;
  /** 32-hex id that is actually on the wire (existing header wins). */
  operationId: string;
  traceparent: string;
}

/**
 * Merge a minted traceparent into fetch arguments without mutating the
 * caller's Request. If a traceparent is already present it is left alone and
 * its operation id is returned — callers must record that effective id, not
 * the minted one they passed in.
 */
export const mergeRynReplayTraceparent = (
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  mintedTraceparent: string
): IRynReplayTraceparentMergeResult => {
  const headers = new Headers(
    init?.headers ??
      (typeof Request !== 'undefined' && input instanceof Request
        ? input.headers
        : undefined)
  );
  const existing = headers.get(RYN_REPLAY_TRACEPARENT_HEADER);
  if (!existing) {
    headers.set(RYN_REPLAY_TRACEPARENT_HEADER, mintedTraceparent);
    const operationId = normalizeRynReplayOperationId(mintedTraceparent);
    if (!operationId) {
      throw new Error('minted traceparent must normalize to a 32-hex operation id');
    }
    return {
      init: { ...init, headers },
      operationId,
      traceparent: mintedTraceparent,
    };
  }

  return {
    init: { ...init, headers },
    operationId:
      normalizeRynReplayOperationId(existing) ??
      normalizeRynReplayOperationId(mintedTraceparent) ??
      existing,
    traceparent: existing,
  };
};
